import React, { ReactNode, createContext, useContext } from 'react';
import { getCookie } from '@datadog/browser-core';
import { useQueryContext } from 'lendingApi/lendingApiHooks';

type TSplitTests = {
  hasAccessToPrimeLoans: boolean;
  allow24mFlexiLoanTerm: boolean;
  hasNewSignUpOnContext: boolean;
  hasMultipleOffersOnContext: boolean;
};

export type TIntroducerContext = {
  isLoggedIn: boolean;
  splitTests: TSplitTests | null;
  introducerId: string | null;
  isError: boolean;
  isLoading: boolean;
};

export const IntroducerContext = createContext<TIntroducerContext>({
  isLoggedIn: false,
  splitTests: null,
  isError: false,
  isLoading: true,
  introducerId: null,
});

export const IntroducerContextProvider = ({
  children,
}: {
  children: ReactNode;
}) => {
  const { data: queryContext, isLoading, isError } = useQueryContext();
  const splitTests: TSplitTests | null = isError
    ? null
    : {
        hasNewSignUpOnContext:
          queryContext?.data?.introducer_account_new_sign_up || false,
        hasMultipleOffersOnContext:
          queryContext?.data
            ?.introducer_portal_multiple_formal_confirmed_offers || false,
        hasAccessToPrimeLoans:
          // TODO: Remove the 'any' when lapi is updated with the new tests
          (queryContext?.data as any)?.access_to_prime_loans || false,
        allow24mFlexiLoanTerm:
          queryContext?.data?.allow_24m_flexi_loan_term || false,
      };

  return (
    <IntroducerContext.Provider
      value={{
        isLoggedIn: getCookie('isLoggedIn') === 'True',
        splitTests,
        isLoading,
        isError,
        introducerId: queryContext?.data?.introducer_id || null,
      }}
    >
      {children}
    </IntroducerContext.Provider>
  );
};

export const useIntroducerContext = (): TIntroducerContext => {
  const context = useContext(IntroducerContext);
  if (!context) {
    throw new Error(
      'useIntroducer must be used within a IntroducerContextProvider',
    );
  }
  return context;
};
